/** Typed client for `/admin`. Separate module from `api.ts` so the staff
 * surface never lands in the bundle path a normal session cares about. */

import { request, type Tier } from './api'

export interface Page<T> {
  total: number
  limit: number
  offset: number
  items: T[]
}

export interface AdminUserRow {
  id: string
  email: string
  display_name: string
  tier: Tier
  default_radius_m: number
  is_admin: boolean
  is_super_admin: boolean
  suspended_at: string | null
  suspended_reason: string | null
  last_seen_at: string | null
  created_at: string
  reminders_active: number
  reminders_total: number
  household_count: number
  push_sub_count: number
}

export interface AdminCategoryRow {
  id: string
  slug: string
  label: string
  color: string
  icon: string
  reminder_count: number
}

export interface AdminReminderRow {
  id: string
  title: string
  category: string
  merchant_class: string
  merchant_label: string
  radius_m: number
  active: boolean
  completed_at: string | null
  snoozed_until: string | null
  created_at: string
  owner_id: string
  owner_email: string
  owner_name: string
  household_id: string | null
  household_name: string | null
  trigger_count: number
}

export interface AdminHouseholdRow {
  id: string
  name: string
  invite_code: string
  owner_id: string
  owner_email: string | null
  created_at: string
  member_count: number
  reminder_count: number
}

export interface AdminHouseholdDetail extends AdminHouseholdRow {
  members: {
    user_id: string
    email: string
    display_name: string
    role: string
    joined_at: string
  }[]
}

export interface AdminTriggerRow {
  id: string
  fired_at: string
  cleared_at: string | null
  user_id: string
  user_email: string | null
  reminder_id: string
  reminder_title: string | null
  store_id: string
  store_name: string | null
}

export interface AdminUserDetail extends AdminUserRow {
  admin_note: string | null
  categories: AdminCategoryRow[]
  households: AdminHouseholdRow[]
  reminders: AdminReminderRow[]
  recent_triggers: AdminTriggerRow[]
  last_position: {
    lat: number
    lon: number
    accuracy_m: number | null
    reported_at: string
  } | null
  push_endpoints: string[]
}

export interface AdminStoreRow {
  id: string
  osm_id: string
  name: string | null
  brand: string | null
  shop: string | null
  amenity: string | null
  leisure: string | null
  address: string | null
  opening_hours: string | null
  lat: number
  lon: number
  fetched_at: string
}

export interface AdminAuditRow {
  id: string
  actor_id: string | null
  actor_email: string
  action: string
  target_type: string | null
  target_id: string | null
  summary: string
  detail: Record<string, unknown> | null
  ip: string | null
  created_at: string
}

export interface AdminOverview {
  generated_at: string
  users: Record<string, number>
  reminders: Record<string, number>
  engine: Record<string, number>
}

export interface AdminTimeseriesPoint {
  day: string
  signups: number
  reminders_created: number
  reminders_completed: number
  triggers: number
}

export interface AdminLeaderRow {
  key: string
  label: string
  reminders: number
  users: number
  active: number | null
}

export interface AdminSystemOut {
  database: string
  postgres_version: string
  postgis_version: string | null
  alembic_revision: string | null
  database_size: string
  connections: number
  push_configured: boolean
  vapid_subject: string
  cors_origins: string[]
  overpass_mirrors: string[]
  free_tier_reminder_limit: number
  geofence_clear_factor: number
  store_cache_ttl_seconds: number
  api_version: string
  server_time: string
  tables: { name: string; live_rows: number | null; bytes: number }[]
}

export interface AdminEmailTest {
  sent: boolean
  configured: boolean
  detail: string
  smtp_host: string
  smtp_port: number
  smtp_user: string
  email_from: string
  app_base_url: string
}

export interface AdminMirrorProbe {
  url: string
  ok: boolean
  status: number | null
  latency_ms: number | null
  error: string | null
}

export interface AdminBroadcastOut {
  dry_run: boolean
  push_configured: boolean
  targeted_users: number
  subscriptions: number
  delivered: number
  pruned: number
}

export type UserSort =
  | 'created_desc'
  | 'created_asc'
  | 'seen_desc'
  | 'email_asc'
  | 'reminders_desc'
  | 'name_asc'

export type ReminderState =
  | 'all'
  | 'active'
  | 'paused'
  | 'completed'
  | 'snoozed'
  | 'shared'

/** Only defined keys become query parameters — an empty filter must not narrow
 * the result to rows whose column literally equals "undefined". */
const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') s.set(k, String(v))
  }
  const out = s.toString()
  return out ? `?${out}` : ''
}

export const adminApi = {
  overview: () => request<AdminOverview>('/admin/overview'),

  timeseries: (days = 30) =>
    request<AdminTimeseriesPoint[]>(`/admin/timeseries${qs({ days })}`),

  topMerchants: (limit = 8) =>
    request<AdminLeaderRow[]>(`/admin/leaders/merchants${qs({ limit })}`),

  topCategories: (limit = 8) =>
    request<AdminLeaderRow[]>(`/admin/leaders/categories${qs({ limit })}`),

  users: (p: {
    q?: string
    tier?: Tier
    admins_only?: boolean
    suspended?: boolean
    sort?: UserSort
    limit?: number
    offset?: number
  }) => request<Page<AdminUserRow>>(`/admin/users${qs(p)}`),

  user: (id: string) => request<AdminUserDetail>(`/admin/users/${id}`),

  patchUser: (
    id: string,
    patch: Partial<{
      display_name: string
      tier: Tier
      default_radius_m: number
      admin_note: string
      is_admin: boolean
      is_super_admin: boolean
      suspended: boolean
      suspended_reason: string
    }>,
  ) =>
    request<AdminUserDetail>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  setPassword: (id: string, password: string) =>
    request<void>(`/admin/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  deleteUser: (id: string) => request<void>(`/admin/users/${id}`, { method: 'DELETE' }),

  clearPosition: (id: string) =>
    request<void>(`/admin/users/${id}/positions/clear`, { method: 'POST' }),

  reminders: (p: {
    q?: string
    owner_id?: string
    merchant_class?: string
    category?: string
    state?: ReminderState
    sort?: string
    limit?: number
    offset?: number
  }) => request<Page<AdminReminderRow>>(`/admin/reminders${qs(p)}`),

  setReminderActive: (id: string, active: boolean) =>
    request<AdminReminderRow>(`/admin/reminders/${id}${qs({ active })}`, { method: 'PATCH' }),

  deleteReminder: (id: string) =>
    request<void>(`/admin/reminders/${id}`, { method: 'DELETE' }),

  households: (p: { q?: string; limit?: number; offset?: number }) =>
    request<Page<AdminHouseholdRow>>(`/admin/households${qs(p)}`),

  household: (id: string) => request<AdminHouseholdDetail>(`/admin/households/${id}`),

  deleteHousehold: (id: string) =>
    request<void>(`/admin/households/${id}`, { method: 'DELETE' }),

  stores: (p: {
    q?: string
    merchant_class?: string
    stale_days?: number
    limit?: number
    offset?: number
  }) => request<Page<AdminStoreRow>>(`/admin/stores${qs(p)}`),

  purgeStores: (body: {
    older_than_days?: number
    brand?: string
    merchant_class?: string
    include_query_cache?: boolean
  }) =>
    request<{ stores_deleted: number; queries_deleted: number }>('/admin/stores/purge', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  triggers: (p: {
    user_id?: string
    open_only?: boolean
    limit?: number
    offset?: number
  }) => request<Page<AdminTriggerRow>>(`/admin/triggers${qs(p)}`),

  audit: (p: { action?: string; actor_id?: string; target_id?: string; limit?: number; offset?: number }) =>
    request<Page<AdminAuditRow>>(`/admin/audit${qs(p)}`),

  auditActions: () => request<string[]>('/admin/audit/actions'),

  system: () => request<AdminSystemOut>('/admin/system'),

  overpassProbe: () =>
    request<AdminMirrorProbe[]>('/admin/system/overpass-probe', { method: 'POST' }),

  emailTest: (email: string) =>
    request<AdminEmailTest>('/admin/system/email-test', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  broadcast: (body: {
    title: string
    body: string
    url?: string
    audience?: 'all' | 'tier' | 'admins' | 'users'
    tier?: Tier
    user_ids?: string[]
    dry_run: boolean
  }) =>
    request<AdminBroadcastOut>('/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}
